import { useState, useEffect, useCallback, useRef } from "react";
import axiosInstance from "@/utils/appsettings";
import { isTrustedBackendOrigin } from "@/utils/postMessageOrigin";

type StripeMode = "test" | "live";

interface UseStripeOAuthConnectOptions {
  mode?: StripeMode;
  onConnected?: () => void;
  onFailed?: (error?: string) => void;
  onPopupBlocked?: () => void;
  onOpened?: () => void;
  onOpenError?: () => void;
}

/** Popup + postMessage Stripe Connect OAuth flow, shared by the chat onboarding panel and the project settings modal. */
export function useStripeOAuthConnect(
  projectId: string | null | undefined,
  options: UseStripeOAuthConnectOptions = {}
) {
  const mode = options.mode ?? "test";
  const [connecting, setConnecting] = useState(false);
  const { onConnected, onFailed, onPopupBlocked, onOpened, onOpenError } = options;
  // A fresh window name per attempt — reusing the same name across repeated clicks makes some
  // browsers treat it as "navigate an existing window" rather than "open a new popup", which can
  // fall back to opening a tab instead of a floating window.
  const attemptCounterRef = useRef(0);

  useEffect(() => {
    if (!connecting || !projectId) return;

    const handleMessage = (event: MessageEvent) => {
      // The OAuth result is posted by the backend-served callback page (a
      // different origin than the SPA), so accept trusted Joylo/backend origins
      // only — otherwise any page could spoof a "connected" result and flip the
      // UI into a connected state.
      if (!isTrustedBackendOrigin(event.origin)) return;
      if (
        event.data?.type !== "stripe_oauth_result" ||
        event.data?.project_id !== projectId
      ) return;

      // Bring this tab back into focus regardless of whether the child was rendered as a
      // floating popup or a plain tab. A real popup closing naturally reveals the tab behind
      // it, but when the browser instead opened a tab (its choice, not fully controllable via
      // window.open features), closing it just follows normal tab-closing behavior — jumping
      // focus to whichever tab is adjacent, not necessarily back to this one. window.focus()
      // works the same way in both cases, so this is the reliable fix rather than the popup
      // window features, which some browsers only partially honor.
      try { window.focus(); } catch { /* no-op */ }

      if (event.data.connected) {
        window.dispatchEvent(new CustomEvent("joylo:stripe_connected", { detail: { projectId } }));
        setConnecting(false);
        onConnected?.();
      } else {
        setConnecting(false);
        onFailed?.(event.data.error);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [connecting, projectId, onConnected, onFailed]);

  const connect = useCallback(async () => {
    if (!projectId) return;

    // Explicit toolbar/menubar/location/status=no (plus a centered position) is
    // what makes browsers render this as a real floating popup window instead of
    // falling back to a new tab — without them some browsers open a tab, which
    // makes the flow look like "the whole page navigated away and didn't come back".
    const width = 600;
    const height = 700;
    const left = Math.round(window.screenX + (window.outerWidth - width) / 2);
    const top = Math.round(window.screenY + (window.outerHeight - height) / 2);
    const features = `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,location=no,status=no,scrollbars=yes,resizable=yes,noopener=no`;

    // Open the popup synchronously inside the click handler so Safari doesn't
    // treat it as a script-initiated popup and block it. We navigate it to the
    // real URL after the fetch resolves. A fresh name per attempt (mode + counter)
    // so connecting test/live don't collide, and so repeat clicks (e.g. Reconnect)
    // always open a genuinely new popup rather than reusing/navigating a prior one.
    attemptCounterRef.current += 1;
    const popup = window.open("", `stripe_oauth_${mode}_${attemptCounterRef.current}`, features);

    if (!popup) {
      onPopupBlocked?.();
      return;
    }

    try {
      const { data } = await axiosInstance.get(`/stripe/connect/redirect?project_id=${projectId}&mode=${mode}`);
      popup.location.href = data.url;
      popup.focus();
      setConnecting(true);
      onOpened?.();
    } catch {
      popup.close();
      onOpenError?.();
    }
  }, [projectId, mode, onPopupBlocked, onOpened, onOpenError]);

  const cancel = useCallback(() => setConnecting(false), []);

  return { connecting, connect, cancel };
}
